/**
 * i18next initialization for autoflo.
 *
 * English-only at launch. The infrastructure is ready for French and
 * Spanish — to add a locale, drop `src/locales/{fr,es}.json` next to
 * en.json, import it here, add it to `resources` and `SUPPORTED_LNGS`,
 * and (for dayjs relative-time) import the matching dayjs locale file
 * in src/lib/dates.ts.
 *
 * Browser language detection runs but is constrained to languages we've
 * actually shipped — an unsupported detected locale falls back to 'en'.
 * `nonExplicitSupportedLngs: true` means `en-CA` / `en-US` keep their
 * region in `i18n.language` (so `Intl.NumberFormat` / dayjs can
 * specialize per region) but reuse the generic `en` resource bundle.
 *
 * Namespace structure: one namespace per page (`dealers`, `suppliers`,
 * `customers`, `vehicles`, `calls`, `estimates`, `settings`) plus
 * `common` (shared chips, statuses, generic UI), `nav` (top-nav link
 * names), and `auth` (Clerk shell wrapper, currently empty).
 */

import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import en from '@/locales/en.json'

const SUPPORTED_LNGS = ['en'] // grow to ['en', 'fr', 'es'] when those resource files land

const NAMESPACES = [
  'common',
  'nav',
  'auth',
  'dashboard',
  'dealers',
  'suppliers',
  'customers',
  'vehicles',
  'calls',
  'estimates',
  'messaging',
  'settings',
  'tags',
  'voice_messages',
  'voice_recorder',
  'whats_new',
  'chat_input',
  'eric_inline',
  'shell',
  'receipts',
  'today',
  'activity',
] as const

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en,
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LNGS,
    // 'en-CA' / 'en-US' resolve to the 'en' bundle but i18n.language
    // keeps the region tag so locale-aware formatters can specialize.
    nonExplicitSupportedLngs: true,
    ns: NAMESPACES,
    defaultNS: 'common',
    interpolation: {
      // React already escapes interpolated values.
      escapeValue: false,
    },
    detection: {
      // Read from the browser's Accept-Language only. We don't persist
      // user choice yet — that needs a Settings preference and a
      // setLanguage() entrypoint, which is forward work.
      order: ['navigator'],
      caches: [],
    },
    returnNull: false,
  })

export default i18n
